import { computed, ref, watch, Ref } from 'vue'
import {
    getActiveLayoutColors,
    getStatusConfiguration,
    getStatusThresholdSound,
    isStatusAlertsDisabled
} from '@/composables/globalData/useLayouts'
import { removeSoundsFromQueueByIdWithHistoryKeep, setupSoundQueue } from '@/composables/useSoundsStoreData'
import i18n from '@/i18n'
import { Extension } from '@/types/extension'
import { limitThresholdValue, warningThresholdValue } from '@/composables/useSoundsThresholdNotification'

export type AlertLevel = '' | 'warning' | 'limit'

export interface AlertState {
    alertColor: Ref<string>
    alertLevel: Ref<AlertLevel>
    iconTimerFillColor: Ref<string>
}

export interface UseExtensionCardAlertsOptions {
    extension: Ref<Extension>
    timerState: Ref<number>
    timerDisplayTime: Ref<string>
    statusColor: Ref<string>
}

export interface UseExtensionCardAlertsReturn extends AlertState {
    timerColor: Ref<string>
    cardAlertStyle: Ref<Record<string, string>>
}

/**
 * Composable for handling extension card alert logic
 * Manages warning/limit thresholds, colors, and sound notifications
 */
export function useExtensionCardAlerts (options: UseExtensionCardAlertsOptions): UseExtensionCardAlertsReturn {
    const { extension, timerState, timerDisplayTime, statusColor } = options

    // Local state
    const alertColor = ref('')
    const alertLevel = ref<AlertLevel>('')

    const isAlertDisabled = computed(() => {
        return isStatusAlertsDisabled(extension.value.representativeStatus)
    })

    // Get layout colors for alerts
    const limitsColors = computed(() => {
        const { loginStatusLimit, loginStatusWarning } = getActiveLayoutColors()
        return {
            loginStatusWarning,
            loginStatusLimit
        }
    })

    // Get status-specific limits
    const statusLimits = computed(() => {
        const statusConfig = getStatusConfiguration(extension.value.representativeStatus)
        return {
            warn: +statusConfig.warning,
            alert: +statusConfig.limit,
            warningSound: statusConfig.warningSound,
            limitSound: statusConfig.limitSound
        }
    })

    // Icon fill color with opacity
    const iconTimerFillColor = computed(() =>
        alertColor.value ? alertColor.value + '19' : ''
    )

    // Timer color: alert color when in alert, otherwise status color
    const timerColor = computed(() =>
        alertLevel.value ? alertColor.value : statusColor.value
    )

    // CSS variables for alert backgrounds
    const cardAlertStyle = computed(() => {
        const style: Record<string, string> = {}

        if (alertColor.value) {
            style['--alert-color'] = alertColor.value
            style['--alert-color-light'] = alertColor.value + '0D' // 5% opacity
            style['--alert-color-medium'] = alertColor.value + '1A' // 10% opacity
        }

        return style
    })

    const loginStatusWarningSound = computed(() => {
        return getStatusThresholdSound(statusLimits.value.warningSound, warningThresholdValue)
    })

    const loginStatusLimitSound = computed(() => {
        return getStatusThresholdSound(statusLimits.value.limitSound, limitThresholdValue)
    })
    /**
     * Setup sound notification for alert level
     */
    function setupSoundNotification (level: string, soundPlayed: boolean) {
        const soundItem = {
            id: extension.value.userID,
            sound: level === limitThresholdValue ? loginStatusLimitSound.value : loginStatusWarningSound.value,
            played: soundPlayed,
            isPlaying: false,
            timestamp: Date.now(),
            notificationHTML: i18n.global.t('widget.extensionCardHtml', {
                entity_name: extension.value.userName,
                current_value: timerDisplayTime.value,
                status: level
            })
        }
        if (soundItem.sound) {
            setupSoundQueue(soundItem)
        }
    }

    // Watch timer state for alert changes
    watch(() => timerState.value, (val) => {
        let newAlertColor = ''
        let newAlertLevel: AlertLevel = ''


        // Check thresholds (limit takes priority over warning) only if alerts are not disabled
        if (!isAlertDisabled.value && statusLimits.value?.alert && val >= statusLimits.value.alert) {
            newAlertColor = limitsColors.value.loginStatusLimit
            newAlertLevel = limitThresholdValue
        } else if (!isAlertDisabled.value && statusLimits.value?.warn && val >= statusLimits.value.warn) {
            newAlertColor = limitsColors.value.loginStatusWarning
            newAlertLevel = warningThresholdValue
        }

        // Update alert color if changed
        if (alertColor.value !== newAlertColor) {
            alertColor.value = newAlertColor
        }

        // Handle sound notifications when alert level changes
        if (alertLevel.value !== newAlertLevel) {
            // Remove sounds if going back to a lower level
            if (alertLevel.value) {
                removeSoundsFromQueueByIdWithHistoryKeep(extension.value.userID)
            }

            // Setup new sound if entering an alert level
            if (newAlertLevel) {
                const shouldSoundNotBePlayed = alertLevel.value === limitThresholdValue && newAlertLevel === warningThresholdValue
                setupSoundNotification(newAlertLevel, shouldSoundNotBePlayed)
            }

            alertLevel.value = newAlertLevel
        }
    })

    return {
        alertColor,
        alertLevel,
        iconTimerFillColor,
        timerColor,
        cardAlertStyle
    }
}
